// Prospection de partenaires (D-111) : accès aux données. Les règles sont dans la base : le bureau seul lit la
// base et le suivi ; les adultes du club ne lisent que leur carte « Qui connaît ? » (la rotation de D-118) ; les mineurs,
// les comptes partenaires et le bureau n'ont pas de carte. Tout s'écrit par des fonctions (partner_prospect_*).
import { supabase } from './supabase';
import type { NoteKind, PitchFigures, RotCounts, RotMode, RotState, Stage } from '../domain/partner-prospect';
import type { RegistryRow } from '../domain/registre';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

// --- Le bureau -------------------------------------------------------------------------------------------
export interface PzCompany {
  id: string; club_id: string; source: 'registre' | 'piste' | 'ancien' | 'manuel'; siret: string | null; name: string;
  naf: string | null; naf_label: string | null; size_code: string | null; address: string | null; distance_m: number | null;
  read_on: string | null; lead_id: string | null; partner_id: string | null; stage: Stage | null; stage_at: string | null;
  owner_id: string | null; next_label: string | null; next_note: string | null; next_on: string | null; next_time: string | null;
  remind_at: string | null; later_until: string | null; later_note: string | null; qc_at: string | null; created_at: string; company_key: string | null;
  /** « Qui connaît ? » (D-118) : prioritaire (en tête de la rotation), retirée de la rotation par le bureau. */
  rot_priority_at: string | null; rot_out_at: string | null;
}
const COLS = 'id, club_id, source, siret, name, naf, naf_label, size_code, address, distance_m, read_on, lead_id, partner_id, stage, stage_at, owner_id, next_label, next_note, next_on, next_time, remind_at, later_until, later_note, qc_at, created_at, company_key, rot_priority_at, rot_out_at';
/** Toute la base du club (5 000 entreprises au plus), sans les logos. */
export async function pzCompanies(clubId: string): Promise<PzCompany[]> {
  const out: PzCompany[] = [];
  for (let from = 0; from < 5000; from += 1000) {
    const { data, error } = await db().from('partner_prospects').select(COLS).eq('club_id', clubId).order('name').range(from, from + 999);
    if (error) throw error;
    out.push(...((data ?? []) as PzCompany[]));
    if ((data ?? []).length < 1000) break;
  }
  return out;
}
/** Les fiches « à contacter » (la tuile du hub). */
export async function pzTodoCount(clubId: string): Promise<number> {
  const { count, error } = await db().from('partner_prospects').select('id', { count: 'exact', head: true }).eq('club_id', clubId).eq('stage', 'a_contacter');
  if (error) throw error;
  return count ?? 0;
}

export interface PzBureau { id: string; first: string | null; title: string | null }
export interface PzIntro { id: string; company_id?: string; first: string | null; long: string | null; on: string; done_on: string | null }
export interface PzPack { id: string; name: string; price_cents: number | null; content: string | null; chosen: number }
export interface PzHome {
  demo: boolean; today: string; gym: { lat: number; lon: number; label: string | null } | null; read_on: string | null; me: string;
  bureau: PzBureau[]; signed_season: number; intros: PzIntro[]; partner_keys: string[];
  leads: { id: string; source: 'page' | 'lien' | 'contact'; status: string; referrer: string | null }[]; packs: PzPack[];
}
export const pzHome = (clubId: string) => call<PzHome>('partner_prospect_home', { p_club: clubId });

export interface PzEvent { id: string; kind: string; title: string; body: string | null; on: string; who: string | null }
export interface PzContact { id: string; name: string; role: string | null; phone: string | null; email: string | null; note: string | null; by: string | null }
export interface PzFiche {
  company: PzCompany & { logo_url: string | null }; owner: PzBureau | null; events: PzEvent[]; contacts: PzContact[]; intros: PzIntro[];
  lead: { source: string; status: string; referrer: string | null; contact_name: string | null; email: string | null; phone: string | null; message: string | null } | null;
}
export const pzFiche = (id: string) => call<PzFiche>('partner_prospect_fiche', { p_company: id });

export const pzImport = (clubId: string, rows: RegistryRow[], purge: boolean) =>
  call<{ added: number; updated: number; skipped: number; purged: number }>('partner_prospect_import', { p_club: clubId, p_rows: rows, p_purge: purge });
export const pzAdd = (id: string) => call<null>('partner_prospect_add', { p_company: id });
export const pzCreate = (clubId: string, name: string) => call<string>('partner_prospect_create', { p_club: clubId, p_name: name });
export const pzStage = (id: string, stage: Stage | null, until: string | null = null, note: string | null = null) =>
  call<{ stage: Stage | null; partner_id: string | null }>('partner_prospect_stage', { p_company: id, p_stage: stage, p_until: until, p_note: note });
export const pzOwner = (id: string, user: string) => call<null>('partner_prospect_owner', { p_company: id, p_user: user });
export const pzNext = (id: string, label: string | null, note: string | null, on: string | null, time: string | null) =>
  call<{ remind_at: string | null }>('partner_prospect_next', { p_company: id, p_label: label, p_note: note, p_on: on, p_time: time });
export const pzNote = (id: string, kind: NoteKind, body: string | null, day: string | null, done = false) =>
  call<null>('partner_prospect_note', { p_company: id, p_kind: kind, p_body: body, p_title: null, p_day: day, p_done: done });
export const pzContactAdd = (id: string, c: { name: string; role: string | null; phone: string | null; email: string | null; note: string | null }) =>
  call<string>('partner_prospect_contact_add', { p_company: id, p_name: c.name, p_role: c.role, p_phone: c.phone, p_email: c.email, p_note: c.note, p_consent: true });
export const pzContactRemove = (id: string) => call<null>('partner_prospect_contact_remove', { p_contact: id });
export const pzLogo = (id: string, logo: string | null) => call<null>('partner_prospect_logo', { p_company: id, p_logo: logo });
export const pzSigner = (clubId: string, title: string | null) => call<null>('partner_prospect_signer', { p_club: clubId, p_title: title });
export const pzIntroDone = (id: string, done: boolean) => call<null>('partner_prospect_intro_done', { p_intro: id, p_done: done });
export const pzFigures = (clubId: string) => call<PitchFigures>('club_pitch_figures', { p_club: clubId });
/** Le logo déposé d'une entreprise (le kit). */
export async function pzLogoOf(id: string): Promise<string | null> {
  const { data, error } = await db().from('partner_prospects').select('logo_url').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as { logo_url: string | null } | null)?.logo_url ?? null;
}

// --- Les adultes du club : « Qui connaît ? » -------------------------------------------------------------
// Chaque lundi, 3 entreprises, une à la fois (D-118). null : pas de carte pour ce compte.
export interface RotItem { id: string; name: string; naf: string | null; naf_label: string | null; address: string | null }
export interface RotCard {
  state: RotState; week: string; next_week: string; total: number; answered: number;
  index?: number; item?: RotItem;
}
export const rotCard = (clubId: string) => call<RotCard | null>('partner_prospect_rot_card', { p_club: clubId });
/** 👍 'up' « Je peux présenter » ; 👎 'down' « Je ne connais personne ». Rend la carte suivante. */
export const rotAnswer = (id: string, answer: 'up' | 'down') =>
  call<{ answer: 'up' | 'down'; owner: string | null; card: RotCard | null }>('partner_prospect_rot_answer', { p_company: id, p_answer: answer });
/** « Pas cette semaine ». */
export const rotClose = (clubId: string) => call<null>('partner_prospect_rot_close', { p_club: clubId });

// --- Le bureau : la rotation ------------------------------------------------------------------------------
export const pzRotSet = (id: string, mode: RotMode) =>
  call<{ stage: Stage | null; rot_priority_at: string | null; rot_out_at: string | null }>('partner_prospect_rot_set', { p_company: id, p_mode: mode });
export interface RotStats {
  week: string; members: number; pool: number; priority: number;
  this_week: { cards: number; up: number; down: number };
  companies: RotCounts[];
}
export const pzRotStats = (clubId: string, company: string | null = null) => call<RotStats>('partner_prospect_rot_stats', { p_club: clubId, p_company: company });

/** Le prochain match à domicile du club (la scène « App des familles » du kit). */
export async function nextHomeMatch(teamIds: string[]): Promise<{ starts_at: string; team: string; opponent: string } | null> {
  if (!teamIds.length) return null;
  const { data, error } = await db().from('events').select('starts_at, team_id, matches!inner(opponent, is_home)')
    .in('team_id', teamIds).eq('kind', 'match').eq('matches.is_home', true).is('cancelled_at', null)
    .gte('starts_at', new Date().toISOString()).order('starts_at').limit(1);
  if (error) throw error;
  const r = (data ?? [])[0] as { starts_at: string; team_id: string; matches: { opponent: string } | { opponent: string }[] } | undefined;
  if (!r) return null;
  const m = Array.isArray(r.matches) ? r.matches[0] : r.matches;
  return m ? { starts_at: r.starts_at, team: r.team_id, opponent: m.opponent } : null;
}
